import { Capacitor } from '@capacitor/core';
import { RiAddFill, RiCloseLine, RiInformationLine } from '@remixicon/react';
import { useState } from 'react';
import { toast } from 'sonner';
import {
	choosePlatform,
	getCoordinatesForPosition,
	getLastOrFirstName,
	renderPosition,
	shouldRenderFullField,
	transformInitial,
} from '../../../helpers/utils';
import { PlayersPositionDisplay } from '../../../types/enums';
import { DraftPosition } from '../../../types/types';
import { Captain } from '../../Icons/Captain';
import PlayersSelect from '../../Modal/PlayersSelect';
import Tooltip from '../../Tooltip';
import Button from '../../ui/button';
import Toast from '../../ui/toast';
import { PlayersReserve } from '../PlayersReserve';
import { Statistics } from '../Statistics';
import { BaskteballField } from './sportField/baskteball';
import { FootballField } from './sportField/football';
import { SoccerField } from './sportField/soccer';

interface iProps {
	optionTournament: string;
	positions: DraftPosition[];
	players: any;
	draftReplySelect: any;
	countSelectPlayerDraft: any;
	numbersOfKicks: number;
	draftPlayersQtd: any;
	modalityName: string;
	scoutsAverage: any;
	newPlayerCanRepeatTeam?: string;
	onChange: (
		key: string,
		playerId: any,
		teamId: any,
		isReserve: boolean
	) => void;
	removeDraft: (key: any) => void;
	addKicks: (key: any) => void;
}

export const GamePlanField = ({
	optionTournament,
	positions,
	players,
	draftReplySelect,
	numbersOfKicks,
	countSelectPlayerDraft,
	draftPlayersQtd,
	modalityName,
	newPlayerCanRepeatTeam = 'Sim',
	scoutsAverage,
	onChange,
	removeDraft,
	addKicks,
}: iProps) => {
	const [activeModal, setActiveModal] = useState<string | null>(null);
	const [playerStatsOpen, setPlayerStatsOpen] = useState(false);

	const groupedByTeam = players
		.filter((player: any) => {
			if (newPlayerCanRepeatTeam === 'Não') {
				const duplicateTeam = !draftReplySelect.some(
					(item: any) =>
						item.teamId === player.team.id && !item.isReserve
				);
				return duplicateTeam;
			} else {
				return true;
			}
		})
		.filter(
			(player: any) =>
				!draftReplySelect.some(
					(draft: any) =>
						draft.playerId === player.player.id && !draft.isReserve
				)
		);
	const renderNamePlayer = (id: string) => {
		const findPlayer = players.find((i: any) => i.player.id === id);
		if (findPlayer) {
			return getLastOrFirstName(findPlayer.player.name);
		}
		return '';
	};
	const renderPlayerTeamBg = (id: string) => {
		const findPlayer = players.find((i: any) => i.player.id === id);
		if (findPlayer) {
			if (findPlayer.team.image) {
				return findPlayer.team.image;
			}
		}
		return '';
	};
	const playerSelectHolder = draftReplySelect.filter(
		(item: any) => !item.isReserve
	);
	const checkPlayerExists = (playerId: string) => {
		return playerSelectHolder.some((p: any) => p.playerId === playerId);
	};
	const renderField = shouldRenderFullField(positions);
	const layoutFormat = () => {
		if (playerSelectHolder.length === 0) {
			return 'max-h-[75vh] lg:max-h-[75vh] pb-7';
		}
		if (playerSelectHolder.length === 1) {
			return 'max-h-[65vh] lg:max-h-[65vh] pb-7';
		}
		if (playerSelectHolder.length === 2) {
			return 'max-h-[54vh] lg:max-h-[54vh] pb-7';
		}

		return 'max-h-[44vh] lg:max-h-[44vh] pb-7';
	};
	const findplayer = (id: string) => {
		return players.find((i: any) => i.player.id === id);
	};
	const getBackgroundStyle = (selectedPlayer: any) => {
		if (!selectedPlayer) return {};
		return {
			backgroundColor: '#FFFF',
			backgroundImage: `url(${renderPlayerTeamBg(
				selectedPlayer.playerId
			)})`,
			backgroundSize: 'contain',
			backgroundPosition: 'center',
			backgroundRepeat: 'no-repeat',
		};
	};

	const positionRender = positions.map((pos) => {
		return [...Array(pos.quantity)].map((_, index) => {
			const key = `${pos.position}-${index + 1}`;
			const coords = getCoordinatesForPosition(
				pos.position,
				index,
				pos.quantity,
				renderField
			);
			const selectedPlayer = draftReplySelect.find(
				(item: any) => item.key === key && !item.isReserve
			);
			const kicksCount = draftReplySelect.filter(
				(item: any) => item.kicks
			).length;
			const canShowKicksIcon =
				kicksCount < numbersOfKicks || selectedPlayer?.kicks;

			return (
				<div
					key={key}
					className="absolute flex flex-col items-center"
					style={{
						top: coords.top,
						left: coords.left,
						transform: 'translate(-50%, -50%)',
					}}
				>
					<div
						className={
							'relative border-2 w-12 h-12 rounded-full font-bahnschrift shadow-md flex items-center justify-center cursor-pointer ' +
							(selectedPlayer
								? 'text-xs bg-primary border-white text-white'
								: 'text-2xl bg-white border-primary text-primary')
						}
						style={getBackgroundStyle(selectedPlayer)}
						onClick={(e) => {
							e.stopPropagation();
							e.preventDefault();
							setActiveModal(key);
						}}
					>
						{selectedPlayer ? <></> : <RiAddFill size={32} />}

						{selectedPlayer && (
							<div
								className="absolute bottom-0 -right-2 cursor-pointer"
								onClick={(e) => {
									e.stopPropagation();
									e.preventDefault();
									removeDraft(key);
								}}
							>
								<svg
									width="16"
									height="16"
									viewBox="0 0 16 16"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
								>
									<rect
										width="16"
										height="16"
										rx="8"
										fill="white"
									/>
									<path
										d="M7.99999 7.28871L10.4889 4.7998L11.2 5.51091L8.71109 7.99981L11.2 10.4887L10.4889 11.1998L7.99999 8.71092L5.5111 11.1998L4.79999 10.4887L7.28888 7.99981L4.79999 5.51091L5.5111 4.7998L7.99999 7.28871Z"
										fill={
											choosePlatform()
												? '#35168C'
												: '#DF1737'
										}
									/>
								</svg>
							</div>
						)}

						{selectedPlayer &&
							numbersOfKicks !== 0 &&
							canShowKicksIcon && (
								<div
									className="absolute -top-1 -right-1 cursor-pointer"
									onClick={(e) => {
										e.stopPropagation();
										e.preventDefault();
										addKicks(key);
									}}
								>
									<Captain
										colorBg={
											selectedPlayer?.kicks
												? choosePlatform()
													? '#35168C'
													: '#DF1737'
												: '#ffffff'
										}
										colorIcon={
											selectedPlayer?.kicks
												? '#ffffff'
												: choosePlatform()
												? '#35168C'
												: '#DF1737'
										}
										className="w-4 h-4"
									/>
								</div>
							)}
					</div>

					<span className="mt-1 font-bahnschrift text-center text-white text-xs font-normal leading-none">
						{selectedPlayer
							? renderNamePlayer(selectedPlayer.playerId)
							: renderPosition(pos.position)}
					</span>

					<PlayersSelect
						id={key}
						open={activeModal === key}
						onClose={() => setActiveModal(null)}
					>
						<div
							className={
								'flex flex-col justify-between px-6 pb-11 lg:pb-6 gap-4 max-h-[90vh] overflow-y-auto ' +
								(Capacitor.getPlatform() === 'ios'
									? 'pt-safe-offset-6'
									: 'pt-10')
							}
						>
							{playerSelectHolder.length > 0 && (
								<div className="flex flex-col gap-4 ">
									<div>
										<span className="text-[#ececec] text-lg font-normal font-bahnschrift">
											Jogadores Titulares
										</span>
										<span className="text-[#6b6e74] text-xs font-light pl-1 font-bahnschrift">
											({countSelectPlayerDraft}/
											{draftPlayersQtd})
										</span>
									</div>

									<div className="grid max-[375px]:grid-cols-2 grid-cols-3 md:grid-cols-6 lg:grid-cols-8 gap-3 overflow-auto px-2 flex-1">
										{playerSelectHolder
											.filter(
												(p: any) =>
													pos.position ===
													PlayersPositionDisplay[
														findplayer(p.playerId)
															.position
													]
											)
											.map(
												(
													player: any,
													index: number
												) => (
													<div
														key={index}
														className={
															'cursor-pointer bg-custom-gray10 rounded-[5px] shadow' +
															(checkPlayerExists(
																player.playerId
															)
																? ' border-2 border-primary'
																: '')
														}
													>
														<div className="relative">
															{checkPlayerExists(
																player.playerId
															) && (
																<div
																	className={
																		'absolute z-30 w-5 h-5 bg-primary rounded-tl-[2px] rounded-br-[9px] justify-center items-center inline-flex cursor-pointer '
																	}
																	onClick={() => {
																		const playerIdVerify =
																			draftReplySelect.find(
																				(
																					item: any
																				) =>
																					item.playerId ===
																					player.playerId
																			);
																		removeDraft(
																			playerIdVerify.key
																		);
																	}}
																>
																	<RiCloseLine className="w-4 h-4 justify-center items-center flex text-custom-white1" />
																</div>
															)}
														</div>

														<div className="w-full min-h-28 mt-2.5 p-2 justify-between items-center inline-flex flex-col gap-1">
															<div className="w-full flex justify-between items-center mb-1">
																<Statistics
																	optionTournament={
																		optionTournament
																	}
																	playerId={
																		player.playerId
																	}
																	playerStatsOpen={
																		playerStatsOpen
																	}
																	setPlayerStatsOpen={
																		setPlayerStatsOpen
																	}
																/>
																<div className="flex flex-col">
																	<Tooltip
																		icon={
																			<div
																				className={
																					'flex flex-col text-sm font-normal font-bahnschrift leading-3' +
																					(findplayer(
																						player.playerId
																					)
																						.average ===
																					0
																						? ' text-custom-white'
																						: findplayer(
																								player.playerId
																						  )
																								.average <
																						  0
																						? ' text-error'
																						: ' text-success')
																				}
																			>
																				<p className="flex items-center">
																					{findplayer(
																						player.playerId
																					).average.toFixed(
																						2
																					)}
																					<RiInformationLine
																						color={
																							findplayer(
																								player.playerId
																							)
																								.average ===
																							0
																								? '#fff'
																								: findplayer(
																										player.playerId
																								  )
																										.average <
																								  0
																								? '#c13939'
																								: '#27bc5a'
																						}
																						className="ml-[2px] w-3.5 h-3.5"
																					/>
																				</p>
																				{
																					findplayer(
																						player.playerId
																					)
																						.totalGamesSum
																				}
																			</div>
																		}
																		infoIcon="warning"
																		message={`Esta é a média de desempenho do jogador nos últimos ${
																			findplayer(
																				player.playerId
																			)
																				.totalGamesSum
																		} jogos, calculada com base na tabela de pontuação abaixo.`}
																		drafPoints
																		draftScouts={
																			scoutsAverage
																		}
																		totalGamesSum={
																			findplayer(
																				player.playerId
																			)
																				.totalGamesSum
																		}
																		average={
																			findplayer(
																				player.playerId
																			)
																				.average
																		}
																		totalScoreSum={
																			findplayer(
																				player.playerId
																			)
																				.totalScoreSum
																		}
																	/>
																</div>
															</div>
															<div className="flex flex-row items-center justify-center relative">
																{findplayer(
																	player.playerId
																).team.image ? (
																	<img
																		className="w-8 h-8 object-cover -mr-2"
																		src={
																			findplayer(
																				player.playerId
																			)
																				.team
																				.image
																		}
																	/>
																) : (
																	<div className="w-6 h-6 -mr-2 text-white font-bahnschrift text-xs bg-primary items-center justify-center flex">
																		{transformInitial(
																			findplayer(
																				player.playerId
																			)
																				.team
																				.name
																		)}
																	</div>
																)}
																{findplayer(
																	player.playerId
																).player
																	.image ? (
																	<img
																		className="w-8 h-8 rounded-[33.55px] object-cover"
																		src={
																			findplayer(
																				player.playerId
																			)
																				.player
																				.image
																		}
																	/>
																) : (
																	<div className="w-8 h-8 rounded-full text-white font-bahnschrift text-xs bg-primary items-center justify-center flex">
																		{transformInitial(
																			findplayer(
																				player.playerId
																			)
																				.player
																				.name
																		)}
																	</div>
																)}
															</div>
															<div className="text-custom-white min-h-6 text-sm text-center font-normal font-bahnschrift leading-[12px]">
																{
																	findplayer(
																		player.playerId
																	).player
																		.name
																}
															</div>
														</div>
													</div>
												)
											)}
									</div>
								</div>
							)}
							<div className="flex flex-col gap-4 h-fit">
								<div>
									<span className="text-custom-white1 text-lg font-normal font-bahnschrift">
										Monte sua equipe{' '}
									</span>
								</div>

								<div
									className={
										'grid max-[375px]:grid-cols-2 grid-cols-3 md:grid-cols-6 lg:grid-cols-8 gap-3 overflow-auto px-2 flex-1 ' +
										layoutFormat()
									}
								>
									{groupedByTeam
										.filter(
											(draft: any) =>
												PlayersPositionDisplay[
													draft.position
												] === pos.position
										)
										.sort(
											(a: any, b: any) =>
												b.average - a.average
										)
										.map((draft: any, index: number) => {
											return (
												<div
													key={index}
													className={
														'cursor-pointer bg-custom-gray10 rounded-[5px] shadow' +
														(checkPlayerExists(
															draft.player.id
														)
															? ' border-2 border-primary'
															: '')
													}
													onClick={(e) => {
														e.stopPropagation();
														e.preventDefault();

														if (
															checkPlayerExists(
																draft.player.id
															)
														) {
															const playerIdVerify =
																draftReplySelect.find(
																	(
																		item: any
																	) =>
																		item.playerId ===
																		draft
																			.player
																			.id
																);
															removeDraft(
																playerIdVerify.key
															);
														} else {
															if (
																newPlayerCanRepeatTeam ===
																'Não'
															) {
																const playerIdVerify =
																	draft.player
																		.id;
																const newPlayer =
																	players.find(
																		(
																			player: any
																		) =>
																			player
																				.player
																				.id ===
																			playerIdVerify
																	);
																const newTeamId =
																	newPlayer
																		.team
																		.id;

																const duplicateTeam =
																	draftReplySelect
																		.filter(
																			(
																				item: any
																			) =>
																				!item.isReserve
																		)
																		.some(
																			(
																				item: any
																			) => {
																				const selectedPlayer =
																					players.find(
																						(
																							p: any
																						) =>
																							p
																								.player
																								.id ===
																							item.playerId
																					);
																				return (
																					selectedPlayer
																						?.team
																						.id ===
																					newTeamId
																				);
																			}
																		);

																if (
																	duplicateTeam
																) {
																	return toast(
																		<Toast variant="error">
																			Você
																			não
																			pode
																			selecionar
																			mais
																			de 1
																			jogador
																			do
																			mesmo
																			time
																			na
																			escalação
																			principal.
																		</Toast>
																	);
																}
															}
															onChange(
																key,
																draft.player.id,
																draft.team.id,
																false
															);
															setActiveModal(
																null
															);
														}
													}}
												>
													<div className="relative">
														{checkPlayerExists(
															draft.player.id
														) && (
															<div
																className={
																	'absolute z-30 w-5 h-5 bg-primary rounded-tl-[2px] rounded-br-[9px] justify-center items-center inline-flex cursor-pointer '
																}
																onClick={() => {
																	const playerIdVerify =
																		draftReplySelect.find(
																			(
																				item: any
																			) =>
																				item.playerId ===
																				draft
																					.player
																					.id
																		);
																	removeDraft(
																		playerIdVerify.key
																	);
																}}
															>
																<RiCloseLine className="w-4 h-4 justify-center items-center flex text-custom-white1" />
															</div>
														)}
													</div>

													<div className="w-full min-h-28 mt-2.5 p-2 justify-between items-center inline-flex flex-col gap-1">
														<div className="w-full flex justify-between items-center mb-1">
															<Statistics
																optionTournament={
																	optionTournament
																}
																playerId={
																	draft.player
																		.id
																}
																playerStatsOpen={
																	playerStatsOpen
																}
																setPlayerStatsOpen={
																	setPlayerStatsOpen
																}
															/>
															<div className="flex flex-col">
																<Tooltip
																	icon={
																		<div
																			className={
																				'flex flex-col text-sm font-normal font-bahnschrift leading-3' +
																				(draft.average ===
																				0
																					? ' text-custom-white'
																					: draft.average <
																					  0
																					? ' text-error'
																					: ' text-success')
																			}
																		>
																			<p className="flex items-center">
																				{draft.average.toFixed(
																					2
																				)}
																				<RiInformationLine
																					color={
																						draft.average ===
																						0
																							? '#fff'
																							: draft.average <
																							  0
																							? '#c13939'
																							: '#27bc5a'
																					}
																					className="ml-[2px] w-3.5 h-3.5"
																				/>
																			</p>
																			{
																				draft.totalGamesSum
																			}
																		</div>
																	}
																	infoIcon="warning"
																	message={`Esta é a média de desempenho do jogador nos últimos ${draft.totalGamesSum} jogos, calculada com base na tabela de pontuação abaixo.`}
																	drafPoints
																	draftScouts={
																		scoutsAverage
																	}
																	totalGamesSum={
																		draft.totalGamesSum
																	}
																	average={
																		draft.average
																	}
																	totalScoreSum={
																		draft.totalScoreSum
																	}
																/>
															</div>
														</div>
														<div className="flex flex-row items-center justify-center relative">
															{draft.team
																.image ? (
																<img
																	className="w-8 h-8 object-cover -mr-2"
																	src={
																		draft
																			.team
																			.image
																	}
																/>
															) : (
																<div className="w-8 h-8 -mr-2 text-white font-bahnschrift text-xs bg-primary items-center justify-center flex">
																	{transformInitial(
																		draft
																			.team
																			.name
																	)}
																</div>
															)}
															{draft.player
																.image ? (
																<img
																	className="w-8 h-8 rounded-[33.55px] object-cover"
																	src={
																		draft
																			.player
																			.image
																	}
																/>
															) : (
																<div className="w-8 h-8 rounded-full text-white font-bahnschrift text-xs bg-primary items-center justify-center flex">
																	{transformInitial(
																		draft
																			.player
																			.name
																	)}
																</div>
															)}
														</div>
														<div className="text-custom-white min-h-6 text-sm text-center font-normal font-bahnschrift leading-[12px]">
															{draft.player.name}
														</div>
													</div>
												</div>
											);
										})}
								</div>
							</div>
						</div>
						<div className="flex justify-center bg-black gap-4 fixed bottom-2 px-3 w-full lg:w-[65%] pb-7">
							<Button
								className="px-5 py-3 border border-custom-white w-full"
								variant="black"
								textCustom="text-custom-white"
								onClick={() => setActiveModal(null)}
							>
								Fechar
							</Button>
						</div>
					</PlayersSelect>
				</div>
			);
		});
	});

	return (
		<div className="flex flex-col gap-2.5">
			<div className="flex flex-col gap-3 overflow-auto">
				<div className="flex flex-col items-center">
					{modalityName === 'Futebol' && (
						<SoccerField renderField={renderField}>
							{positionRender}
						</SoccerField>
					)}
					{modalityName === 'Basquete' && (
						<BaskteballField>{positionRender}</BaskteballField>
					)}
					{modalityName === 'Futebol Americano' && (
						<FootballField>{positionRender}</FootballField>
					)}
				</div>
			</div>

			<PlayersReserve
				optionTournament={optionTournament}
				positions={positions}
				players={players.filter(
					(player: any) =>
						!draftReplySelect.some(
							(draft: any) =>
								draft.playerId === player.player.id &&
								!draft.isReserve
						)
				)}
				scoutsAverage={scoutsAverage}
				draftReplySelect={draftReplySelect}
				onChange={onChange}
				removeDraft={removeDraft}
			/>
		</div>
	);
};
